import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAppDispatch, useAppSelector } from "@/hooks/useStore";
import { selectIsAuthenticated, setToken } from "@/store/slices/auth";
import { authService } from "@/services/auth.service";
import { toast } from "sonner";

function GoogleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"
        fill="#EA4335"
      />
    </svg>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const token = searchParams.get("token");
  const sessionExpired =
    searchParams.get("session_expired") === "1" ||
    (typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("session_expired") === "1");

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/catalog", { replace: true });
      return;
    }

    if (token) {
      dispatch(setToken(token));
      navigate("/catalog", { replace: true });
    }
  }, [token, isAuthenticated, dispatch, navigate]);

  useEffect(() => {
    if (sessionExpired) {
      const id = setTimeout(() => {
        toast.info("Sua sessão expirou. Faça login novamente para continuar.", {
          duration: 6000,
          position: "top-center",
        });

        const url = new URL(window.location.href);
        url.searchParams.delete("session_expired");
        window.history.replaceState({}, "", url.pathname + url.search);
      }, 150);
      return () => clearTimeout(id);
    }
  }, [sessionExpired]);

  const handleGoogleLogin = () => {
    const googleLoginUrl = authService.getGoogleLoginUrl();
    window.location.assign(googleLoginUrl);
  };

  if (isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-[#3B494A] text-base">Redirecionando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="flex justify-center">
          <svg width="136" height="48" viewBox="0 0 136 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M27.3328 34.6693L22.8637 35.8668C22.2607 36.0283 21.67 35.9422 21.0916 35.6083C20.5133 35.2744 20.1433 34.8059 19.9817 34.2029L18.8286 29.8991C19.6055 29.6317 20.1942 29.1439 20.5949 28.4357C20.9957 27.7274 21.0896 26.9759 20.8767 26.1812C20.6637 25.3863 20.2065 24.7823 19.5052 24.3689C18.8041 23.9557 18.0499 23.8261 17.2425 23.98L16.0845 19.6582C15.9216 19.0501 16.0072 18.4574 16.3414 17.8801C16.6756 17.303 17.1442 16.9336 17.7472 16.7721L21.9471 15.6467C21.9021 14.8164 22.0924 14.061 22.518 13.3807C22.9438 12.7005 23.5384 12.2581 24.3021 12.0535C25.0656 11.849 25.8022 11.9357 26.5121 12.3139C27.222 12.6922 27.7644 13.25 28.1394 13.9875L32.292 12.8748C32.9 12.7119 33.4926 12.7969 34.0695 13.1299C34.6464 13.4631 35.0163 13.9337 35.1792 14.5417L36.2919 18.6943C37.1222 18.6492 37.8719 18.8535 38.541 19.307C39.21 19.7608 39.6468 20.3694 39.8514 21.1329C40.056 21.8965 39.9756 22.6222 39.6102 23.3099C39.2447 23.9977 38.6932 24.529 37.9557 24.9039L39.0765 29.0865C39.238 29.6895 39.1525 30.28 38.8198 30.858C38.4869 31.4361 38.0165 31.8066 37.4084 31.9695L32.921 33.1719C32.6718 32.2566 32.1938 31.5703 31.487 31.1128C30.7802 30.6553 30.0413 30.5299 29.2703 30.7365C28.4995 30.943 27.9228 31.421 27.5402 32.1706C27.1574 32.9202 27.0882 33.7531 27.3328 34.6693ZM22.3004 33.5146L24.8747 32.8248C25.0004 31.6578 25.4205 30.6922 26.135 29.9279C26.8495 29.1638 27.6871 28.6531 28.6479 28.3957C29.5961 28.1416 30.5767 28.1651 31.5897 28.4663C32.6028 28.7677 33.4626 29.3904 34.1691 30.3344L36.7201 29.6509L35.0628 23.4656C34.9725 23.1289 35.0317 23.3496 34.9725 23.1289C34.8884 22.8149 34.8757 22.7674 34.8008 22.4881C34.726 22.2087 34.7201 22.187 34.6136 21.7895C34.5519 21.559 34.5519 21.559 34.5388 21.5104L32.8563 15.2312L26.6489 16.8945C26.5285 16.9267 26.6489 16.8945 26.3694 16.9693C26.2146 17.0108 25.9504 17.0816 25.6711 17.1565C25.3918 17.2313 25.2727 17.2632 24.9728 17.3436C24.6729 17.4239 24.9587 17.3474 24.6933 17.4184L18.4367 19.0949L19.1198 21.6444C20.102 21.9052 20.9646 22.3753 21.7074 23.0545C22.4503 23.7338 22.9543 24.5682 23.2195 25.5579C23.4819 26.5372 23.4628 27.5074 23.1623 28.4687C22.8618 29.4298 22.3464 30.2605 21.6162 30.961L22.3004 33.5146Z" fill="#BAC9CA"/>
            <path d="M113.317 32.8552C112.004 32.8552 110.871 32.5825 109.916 32.037C108.968 31.4859 108.237 30.7075 107.726 29.7018C107.215 28.6904 106.959 27.5001 106.959 26.1307C106.959 24.7842 107.215 23.6023 107.726 22.5853C108.243 21.5626 108.965 20.7671 109.891 20.1989C110.817 19.6251 111.905 19.3381 113.155 19.3381C113.962 19.3381 114.723 19.4688 115.439 19.7302C116.161 19.9859 116.797 20.3836 117.348 20.9234C117.905 21.4631 118.343 22.1506 118.661 22.9859C118.979 23.8154 119.138 24.804 119.138 25.9518V26.8978H108.408V24.8182H116.181C116.175 24.2273 116.047 23.7018 115.797 23.2415C115.547 22.7756 115.198 22.4092 114.749 22.1421C114.306 21.8751 113.789 21.7415 113.198 21.7415C112.567 21.7415 112.013 21.895 111.536 22.2018C111.058 22.5029 110.686 22.9006 110.419 23.395C110.158 23.8836 110.024 24.4205 110.019 25.0057V26.8211C110.019 27.5825 110.158 28.2359 110.436 28.7813C110.715 29.3211 111.104 29.7359 111.604 30.0256C112.104 30.3097 112.689 30.4518 113.36 30.4518C113.808 30.4518 114.215 30.3893 114.578 30.2643C114.942 30.1336 115.257 29.9432 115.524 29.6932C115.791 29.4432 115.993 29.1336 116.129 28.7643L119.01 29.0881C118.828 29.8495 118.482 30.5143 117.97 31.0825C117.465 31.645 116.817 32.0825 116.027 32.395C115.237 32.7018 114.334 32.8552 113.317 32.8552Z" fill="#BAC9CA"/>
            <path d="M105.069 15.1449V32.5994H101.983V15.1449H105.069Z" fill="#BAC9CA"/>
            <path d="M89.0566 32.5995V30.6392L95.8066 22.1762V22.0654H89.2782V19.5085H99.5822V21.6137L93.1561 29.9318V30.0426H99.8038V32.5995H89.0566Z" fill="#BAC9CA"/>
            <path d="M76.2998 32.5995V30.6392L83.0498 22.1762V22.0654H76.5214V19.5085H86.8254V21.6137L80.3992 29.9318V30.0426H87.047V32.5995H76.2998Z" fill="#BAC9CA"/>
            <path d="M70.9326 27.0938V19.5085H74.0178V32.5995H71.0264V30.2727H70.89C70.5946 31.0057 70.1088 31.6051 69.4326 32.071C68.7622 32.537 67.9355 32.7699 66.9525 32.7699C66.0945 32.7699 65.336 32.5796 64.6769 32.1989C64.0235 31.8125 63.5122 31.2529 63.1428 30.5199C62.7735 29.7813 62.5889 28.8892 62.5889 27.8438V19.5085H65.6741V27.3665C65.6741 28.196 65.9014 28.8551 66.3559 29.3438C66.8105 29.8324 67.4071 30.0767 68.1457 30.0767C68.6002 30.0767 69.0406 29.9659 69.4667 29.7443C69.8928 29.5227 70.2423 29.1932 70.515 28.7557C70.7934 28.3125 70.9326 27.7585 70.9326 27.0938Z" fill="#BAC9CA"/>
            <path d="M48 32.5994V15.1449H54.5455C55.8864 15.1449 57.0114 15.3949 57.9205 15.8949C58.8352 16.3949 59.5256 17.0824 59.9915 17.9574C60.4631 18.8267 60.6989 19.8154 60.6989 20.9233C60.6989 22.0426 60.4631 23.0369 59.9915 23.9063C59.5199 24.7756 58.8239 25.4602 57.9034 25.9602C56.983 26.4546 55.8494 26.7017 54.5028 26.7017H50.1648V24.1023H54.0767C54.8608 24.1023 55.5028 23.9659 56.0028 23.6932C56.5028 23.4205 56.8722 23.0455 57.1108 22.5682C57.3551 22.0909 57.4773 21.5426 57.4773 20.9233C57.4773 20.304 57.3551 19.7585 57.1108 19.2869C56.8722 18.8154 56.5 18.4489 55.9943 18.1875C55.4943 17.9205 54.8494 17.7869 54.0597 17.7869H51.1619V32.5994H48Z" fill="#BAC9CA"/>
          </svg>
        </div>

        <div>
          <h1 className="text-2xl font-semibold text-[#151C1D] tracking-tight">
            Entrar
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Use sua conta Google corporativa para continuar
          </p>
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full h-11 gap-3"
          >
            <GoogleIcon />
            Entrar com Google
          </Button>
        </div>

        <p className="text-xs text-center text-muted-foreground">
          Acesso restrito a contas{" "}
          <span className="font-medium text-[#151C1D]">@sbxgroup</span>
        </p>

        {/* Footer */}
        <p className="text-center text-xs text-[#6B7A7B]">
          © {new Date().getFullYear()} Puzzle. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
}
